import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

const ALERT_PAGE_SIZE = 20;

/**
 * Row shape is inferred from the query itself (never asserted with `as`), so a
 * column missing from `.select()` becomes a compile error rather than a dead button.
 */
const fetchCapacityAlerts = async () => {
  const { data, error, count } = await supabase
    .from("capacity_overrun_alerts")
    .select(
      `
      id, kind, event_id, capacity, booked_before, requested, created_at,
      events(title),
      campaign_packages(name)
    `,
      { count: "exact" },
    )
    .is("resolved_at", null)
    .order("created_at", { ascending: false })
    .limit(ALERT_PAGE_SIZE);
  if (error) throw error;
  return { alerts: data ?? [], total: count ?? (data?.length ?? 0) };
};

type CapacityAlert = Awaited<ReturnType<typeof fetchCapacityAlerts>>["alerts"][number];

const seatsOver = (a: CapacityAlert) => a.booked_before + a.requested - a.capacity;

const formatAlertTime = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Banner for unresolved capacity_overrun_alerts rows: confirmed tickets that
 * oversold an event (kind = 'event') or packages sold past their order limit
 * (kind = 'campaign_package'). Renders nothing when there are none.
 */
const CapacityAlertsBanner = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["capacity-overrun-alerts", "unresolved"],
    queryFn: fetchCapacityAlerts,
    // Keep the banner fresh while Anh works in the admin console.
    refetchInterval: 60_000,
  });

  const alerts = data?.alerts ?? [];
  const hidden = Math.max((data?.total ?? 0) - alerts.length, 0);

  const resolveMutation = useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase
        .from("capacity_overrun_alerts")
        .update({ resolved_at: new Date().toISOString() })
        .eq("id", alertId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capacity-overrun-alerts"] });
    },
  });

  if (isError) {
    return (
      <p className="mb-6 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        Couldn't load capacity warnings. Reload the page to try again.
      </p>
    );
  }

  if (isLoading || alerts.length === 0) return null;

  return (
    <div className="mb-6 space-y-2">
      {alerts.map((alert) => {
        const over = seatsOver(alert);
        const title =
          alert.kind === "campaign_package"
            ? alert.campaign_packages?.name || "A campaign package"
            : alert.events?.title || "An event";
        return (
          <div
            key={alert.id}
            className="flex flex-col gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <p className="font-semibold text-foreground">
                  {alert.kind === "campaign_package"
                    ? `${title} sold ${over} ${over === 1 ? "pass" : "passes"} past its order limit of ${alert.capacity}`
                    : `${title} is ${over} ${over === 1 ? "seat" : "seats"} over capacity`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatAlertTime(alert.created_at)}
                  {alert.kind === "event" && " — someone may need to be moved, refunded, or squeezed in"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 pl-8 sm:pl-0">
              {alert.kind === "event" && alert.event_id && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/events/${alert.event_id}`}>
                    Open event
                    <ExternalLink className="ml-2 h-3.5 w-3.5" />
                  </Link>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => resolveMutation.mutate(alert.id)}
                disabled={resolveMutation.isPending}
              >
                <Check className="mr-2 h-3.5 w-3.5" />
                Mark resolved
              </Button>
            </div>
          </div>
        );
      })}
      {hidden > 0 && (
        <p className="text-xs text-muted-foreground">
          Showing the newest {alerts.length} of {data?.total} unresolved warnings. Resolve older
          ones to see the rest.
        </p>
      )}
    </div>
  );
};

export default CapacityAlertsBanner;
